import { scopeProjectRef, scopeThreadRef } from "@threadlines/client-runtime";
import type { EnvironmentId, ThreadId } from "@threadlines/contracts";
import {
  ChevronDownIcon,
  CloudIcon,
  FolderGit2Icon,
  FolderGitIcon,
  FolderIcon,
  FolderSyncIcon,
  MonitorIcon,
} from "lucide-react";
import { memo, useMemo } from "react";

import { useComposerDraftStore, type DraftId } from "../composerDraftStore";
import { useIsMobile } from "../hooks/useMediaQuery";
import { useStore } from "../store";
import { createProjectSelectorByRef, createThreadSelectorByRef } from "../storeSelectors";
import {
  type EnvMode,
  type EnvironmentOption,
  resolvePendingCheckoutSwitch,
  resolveActiveWorktreePath,
  resolveCurrentWorkspaceLabel,
  resolveEnvModeLabel,
  resolveEffectiveEnvMode,
  resolveLockedWorkspaceLabel,
  resolveRunSentence,
} from "./BranchToolbar.logic";
import { BranchToolbarBranchSelector } from "./BranchToolbarBranchSelector";
import { BranchToolbarCheckoutSwitchChip } from "./BranchToolbarCheckoutSwitchChip";
import { BranchToolbarEnvironmentSelector } from "./BranchToolbarEnvironmentSelector";
import { BranchToolbarEnvModeSelector } from "./BranchToolbarEnvModeSelector";
import { Button } from "./ui/button";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "./ui/menu";
import { Separator } from "./ui/separator";

interface BranchToolbarProps {
  environmentId: EnvironmentId;
  threadId: ThreadId;
  draftId?: DraftId;
  onEnvModeChange: (mode: EnvMode) => void;
  effectiveEnvModeOverride?: EnvMode;
  activeThreadBranchOverride?: string | null;
  /** The override is the base the "New worktrees from" setting gave, not a pick. */
  activeThreadBranchOverrideFromSetting?: boolean;
  onActiveThreadBranchOverrideChange?: (
    branch: string | null,
    options?: { readonly fromSetting?: boolean },
  ) => void;
  envLocked: boolean;
  onCheckoutPullRequestRequest?: (reference: string) => void;
  onComposerFocusRequest?: () => void;
  availableEnvironments?: readonly EnvironmentOption[];
  onEnvironmentChange?: (environmentId: EnvironmentId) => void;
  /**
   * Offers "Set up on another computer…" in the "Runs on" picker, which then
   * shows even while the project is on one computer only.
   */
  onSetUpElsewhere?: () => void;
  /**
   * The branch a new worktree is being cut from for the thread's first
   * message. While it is set the row says so, in place of choices that can
   * no longer change anything.
   */
  preparingWorktreeFrom?: string | null;
}

interface MobileRunContextSelectorProps {
  envLocked: boolean;
  envModeLocked: boolean;
  environmentId: EnvironmentId;
  availableEnvironments: readonly EnvironmentOption[] | undefined;
  showEnvironmentPicker: boolean;
  onEnvironmentChange: ((environmentId: EnvironmentId) => void) | undefined;
  onSetUpElsewhere: (() => void) | undefined;
  effectiveEnvMode: EnvMode;
  activeWorktreePath: string | null;
  onEnvModeChange: (mode: EnvMode) => void;
}

const MobileRunContextSelector = memo(function MobileRunContextSelector({
  envLocked,
  envModeLocked,
  environmentId,
  availableEnvironments,
  showEnvironmentPicker,
  onEnvironmentChange,
  onSetUpElsewhere,
  effectiveEnvMode,
  activeWorktreePath,
  onEnvModeChange,
}: MobileRunContextSelectorProps) {
  const activeEnvironment = useMemo(
    () => availableEnvironments?.find((env) => env.environmentId === environmentId) ?? null,
    [availableEnvironments, environmentId],
  );
  const WorkspaceIcon =
    effectiveEnvMode === "worktree"
      ? FolderGit2Icon
      : activeWorktreePath
        ? FolderGitIcon
        : FolderIcon;
  const workspaceLabel = envModeLocked
    ? resolveLockedWorkspaceLabel(activeWorktreePath)
    : effectiveEnvMode === "worktree"
      ? resolveEnvModeLabel("worktree")
      : resolveCurrentWorkspaceLabel(activeWorktreePath);
  const isLocked = envLocked || envModeLocked;
  const EnvironmentIcon = activeEnvironment?.isPrimary ? MonitorIcon : CloudIcon;
  const icon = showEnvironmentPicker ? (
    // Button's base styles apply `-mx-0.5` to descendant SVGs, which eats 4px
    // out of whatever gap we set. mx-0! cancels that so gap-0.5 reads as 2px.
    <span className="inline-flex shrink-0 items-center gap-0.5">
      <EnvironmentIcon className="size-3 shrink-0 mx-0!" />
      <WorkspaceIcon className="size-3 shrink-0 mx-0!" />
    </span>
  ) : (
    <WorkspaceIcon className="size-3 shrink-0" />
  );
  const triggerContent = (
    <>
      {icon}
      <span className="min-w-0 truncate">
        {showEnvironmentPicker ? (activeEnvironment?.label ?? "Run on") : workspaceLabel}
      </span>
    </>
  );

  if (isLocked) {
    return (
      <span className="inline-flex min-w-0 max-w-[48%] flex-1 items-center justify-start gap-1 rounded-md border border-transparent px-[calc(--spacing(2)-1px)] text-sm font-medium text-muted-foreground/70 md:hidden">
        {triggerContent}
      </span>
    );
  }

  return (
    <Menu>
      <MenuTrigger
        render={<Button variant="ghost" size="xs" />}
        className="min-w-0 max-w-[48%] flex-1 justify-start text-muted-foreground/70 hover:text-foreground/80 md:hidden"
      >
        {triggerContent}
        <ChevronDownIcon className="size-3 shrink-0 opacity-50" />
      </MenuTrigger>
      <MenuPopup align="start" side="top" className="w-64">
        {showEnvironmentPicker && availableEnvironments && onEnvironmentChange ? (
          <>
            <MenuGroup>
              <MenuGroupLabel>Run on</MenuGroupLabel>
              <MenuRadioGroup
                value={environmentId}
                onValueChange={(value) => onEnvironmentChange(value as EnvironmentId)}
              >
                {availableEnvironments.map((env) => {
                  const Icon = env.isPrimary ? MonitorIcon : CloudIcon;
                  return (
                    <MenuRadioItem
                      key={env.environmentId}
                      disabled={envLocked}
                      value={env.environmentId}
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        <Icon className="size-3" />
                        <span className="min-w-0 truncate">{env.label}</span>
                      </span>
                    </MenuRadioItem>
                  );
                })}
              </MenuRadioGroup>
              {onSetUpElsewhere && !envLocked ? (
                <MenuItem onClick={onSetUpElsewhere} data-testid="runs-on-set-up-elsewhere">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <FolderSyncIcon className="size-3" />
                    <span className="min-w-0 truncate">Set up on another computer…</span>
                  </span>
                </MenuItem>
              ) : null}
            </MenuGroup>
            <MenuSeparator />
          </>
        ) : null}
        <MenuGroup>
          <MenuGroupLabel>Workspace</MenuGroupLabel>
          <MenuRadioGroup
            value={effectiveEnvMode}
            onValueChange={(value) => onEnvModeChange(value as EnvMode)}
          >
            <MenuRadioItem disabled={envModeLocked} value="local">
              <span className="flex min-w-0 items-center gap-1.5">
                {activeWorktreePath ? (
                  <FolderGitIcon className="size-3" />
                ) : (
                  <FolderIcon className="size-3" />
                )}
                <span className="min-w-0 truncate">
                  {resolveCurrentWorkspaceLabel(activeWorktreePath)}
                </span>
              </span>
            </MenuRadioItem>
            <MenuRadioItem disabled={envModeLocked} value="worktree">
              <span className="flex min-w-0 items-center gap-1.5">
                <FolderGit2Icon className="size-3" />
                <span className="min-w-0 truncate">{resolveEnvModeLabel("worktree")}</span>
              </span>
            </MenuRadioItem>
          </MenuRadioGroup>
        </MenuGroup>
      </MenuPopup>
    </Menu>
  );
});

export const BranchToolbar = memo(function BranchToolbar({
  environmentId,
  threadId,
  draftId,
  onEnvModeChange,
  effectiveEnvModeOverride,
  activeThreadBranchOverride,
  activeThreadBranchOverrideFromSetting,
  onActiveThreadBranchOverrideChange,
  envLocked,
  onCheckoutPullRequestRequest,
  onComposerFocusRequest,
  availableEnvironments,
  onEnvironmentChange,
  onSetUpElsewhere,
  preparingWorktreeFrom,
}: BranchToolbarProps) {
  const threadRef = useMemo(
    () => scopeThreadRef(environmentId, threadId),
    [environmentId, threadId],
  );
  const serverThreadSelector = useMemo(() => createThreadSelectorByRef(threadRef), [threadRef]);
  const serverThread = useStore(serverThreadSelector);
  const draftThread = useComposerDraftStore((store) =>
    draftId ? store.getDraftSession(draftId) : store.getDraftThreadByRef(threadRef),
  );
  const activeProjectRef = serverThread
    ? scopeProjectRef(serverThread.environmentId, serverThread.projectId)
    : draftThread
      ? scopeProjectRef(draftThread.environmentId, draftThread.projectId)
      : null;
  const activeProjectSelector = useMemo(
    () => createProjectSelectorByRef(activeProjectRef),
    [activeProjectRef],
  );
  const activeProject = useStore(activeProjectSelector);
  const hasActiveThread = serverThread !== undefined || draftThread !== null;
  const storedWorktreePath = serverThread?.worktreePath ?? draftThread?.worktreePath ?? null;
  const activeWorktreePath = resolveActiveWorktreePath(
    activeProject?.cwd ?? null,
    storedWorktreePath,
  );
  // Display-only: when the agent moved the session's working directory
  // (effectiveCwd diverged), the workspace label follows it so the footer
  // matches the source-control surfaces. Mode/lock semantics stay keyed to
  // the configured worktree.
  const displayWorktreePath = serverThread?.effectiveCwd ?? activeWorktreePath;
  // A checkout switch only takes effect on the next turn, so say so while the
  // live session still runs in the checkout it started in.
  const pendingCheckoutSwitch = resolvePendingCheckoutSwitch({
    sessionCheckoutCwd: serverThread?.session?.checkoutCwd ?? null,
    sessionStatus: serverThread?.session?.orchestrationStatus ?? null,
    pendingBackgroundTaskCount: serverThread?.session?.pendingBackgroundTaskCount ?? 0,
    activeProjectCwd: activeProject?.cwd ?? null,
    activeWorktreePath,
  });
  const effectiveEnvMode =
    effectiveEnvModeOverride ??
    resolveEffectiveEnvMode({
      activeWorktreePath,
      hasServerThread: serverThread !== undefined,
      draftThreadEnvMode: draftThread?.envMode,
    });
  const envModeLocked = envLocked || (serverThread !== undefined && activeWorktreePath !== null);

  // Two computers to pick from, or one and a way to add another.
  const showEnvironmentPicker = Boolean(
    availableEnvironments &&
    onEnvironmentChange &&
    (availableEnvironments.length > 1 || (onSetUpElsewhere && availableEnvironments.length > 0)),
  );
  const isMobile = useIsMobile();

  if (!hasActiveThread || !activeProject) return null;

  // The first message's worktree is being cut: where the thread runs is
  // settled, so the row reads as that work, in the working line's shimmer.
  if (preparingWorktreeFrom) {
    return (
      <div
        className="mx-auto w-full max-w-4xl px-3 pb-3 pt-2 text-xs leading-5 sm:px-3.5 draft-hero:max-w-[45rem]"
        role="status"
        data-testid="branch-toolbar-preparing"
      >
        <span className="working-shimmer">
          Preparing a new worktree from {preparingWorktreeFrom}
        </span>
      </div>
    );
  }

  // A thread that has not started yet states where it will run as a sentence,
  // each choice a picker set in the words. Once it is a real thread the row is
  // status more than choice, and it goes back to compact chips.
  if (serverThread === undefined && !isMobile) {
    const sentence = resolveRunSentence({ effectiveEnvMode, activeWorktreePath });
    return (
      // Running text, not a flex row: the words and pickers wrap like a
      // sentence and read as one to a screen reader or a copy.
      <div
        className="mx-auto w-full max-w-4xl px-3 pb-3 pt-2 text-xs leading-5 text-muted-foreground sm:px-3.5 draft-hero:max-w-[45rem]"
        data-testid="branch-toolbar-sentence"
      >
        Runs{" "}
        {showEnvironmentPicker && availableEnvironments && onEnvironmentChange ? (
          <>
            on{" "}
            <BranchToolbarEnvironmentSelector
              presentation="sentence"
              envLocked={envLocked}
              environmentId={environmentId}
              availableEnvironments={availableEnvironments}
              onEnvironmentChange={onEnvironmentChange}
              onSetUpElsewhere={onSetUpElsewhere}
            />{" "}
          </>
        ) : null}
        in{" "}
        <BranchToolbarEnvModeSelector
          presentation="sentence"
          envLocked={envModeLocked}
          effectiveEnvMode={effectiveEnvMode}
          activeWorktreePath={displayWorktreePath}
          onEnvModeChange={onEnvModeChange}
        />{" "}
        {sentence.branchPreposition}{" "}
        <BranchToolbarBranchSelector
          presentation="sentence"
          environmentId={environmentId}
          threadId={threadId}
          {...(draftId ? { draftId } : {})}
          envLocked={envLocked}
          {...(effectiveEnvModeOverride ? { effectiveEnvModeOverride } : {})}
          {...(activeThreadBranchOverride !== undefined ? { activeThreadBranchOverride } : {})}
          {...(activeThreadBranchOverrideFromSetting
            ? { activeThreadBranchOverrideFromSetting }
            : {})}
          {...(onActiveThreadBranchOverrideChange ? { onActiveThreadBranchOverrideChange } : {})}
          {...(onCheckoutPullRequestRequest ? { onCheckoutPullRequestRequest } : {})}
          {...(onComposerFocusRequest ? { onComposerFocusRequest } : {})}
        />
        .
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl items-center gap-2 px-2.5 pb-3 pt-1 sm:px-3 draft-hero:max-w-[45rem]">
      {isMobile ? (
        <MobileRunContextSelector
          envLocked={envLocked}
          envModeLocked={envModeLocked}
          environmentId={environmentId}
          availableEnvironments={availableEnvironments}
          showEnvironmentPicker={showEnvironmentPicker}
          onEnvironmentChange={onEnvironmentChange}
          onSetUpElsewhere={onSetUpElsewhere}
          effectiveEnvMode={effectiveEnvMode}
          activeWorktreePath={displayWorktreePath}
          onEnvModeChange={onEnvModeChange}
        />
      ) : (
        <div className="flex min-w-0 shrink-0 items-center gap-1">
          {showEnvironmentPicker && availableEnvironments && onEnvironmentChange && (
            <>
              <BranchToolbarEnvironmentSelector
                envLocked={envLocked}
                environmentId={environmentId}
                availableEnvironments={availableEnvironments}
                onEnvironmentChange={onEnvironmentChange}
                onSetUpElsewhere={onSetUpElsewhere}
              />
              <Separator orientation="vertical" className="mx-0.5 h-3.5!" />
            </>
          )}
          <BranchToolbarEnvModeSelector
            envLocked={envModeLocked}
            effectiveEnvMode={effectiveEnvMode}
            activeWorktreePath={displayWorktreePath}
            onEnvModeChange={onEnvModeChange}
          />
        </div>
      )}

      {pendingCheckoutSwitch ? (
        <BranchToolbarCheckoutSwitchChip
          environmentId={environmentId}
          threadId={threadId}
          pendingCheckoutSwitch={pendingCheckoutSwitch}
        />
      ) : null}

      <BranchToolbarBranchSelector
        className={
          pendingCheckoutSwitch
            ? "min-w-0 flex-1 justify-end md:flex-none"
            : "min-w-0 flex-1 justify-end md:ml-auto md:flex-none"
        }
        environmentId={environmentId}
        threadId={threadId}
        {...(draftId ? { draftId } : {})}
        envLocked={envLocked}
        {...(effectiveEnvModeOverride ? { effectiveEnvModeOverride } : {})}
        {...(activeThreadBranchOverride !== undefined ? { activeThreadBranchOverride } : {})}
        {...(activeThreadBranchOverrideFromSetting
          ? { activeThreadBranchOverrideFromSetting }
          : {})}
        {...(onActiveThreadBranchOverrideChange ? { onActiveThreadBranchOverrideChange } : {})}
        {...(onCheckoutPullRequestRequest ? { onCheckoutPullRequestRequest } : {})}
        {...(onComposerFocusRequest ? { onComposerFocusRequest } : {})}
      />
    </div>
  );
});
